/**
 * Aba Resumo Executivo — visão unificada (resultados + operação + sustentação).
 */
import React, { useMemo } from 'react';
import {
  Activity,
  AlertTriangle,
  BarChart2,
  Bell,
  CheckCircle2,
  ClipboardList,
  DollarSign,
  Flag,
  Flame,
  MinusCircle,
  Plus,
  Shield,
  Target,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import { ContextualKPI } from '../analytics/ContextualKPI';
import { BarChartBlueprints } from '../analytics/BarChartBlueprints';
import type { EnterpriseProject, ProjectHealth } from '../../types/enterpriseProject';
import type { ProjectPlansSummary } from '../../types/projectPlan';
import type { RoadmapItem, SustainabilityAudit } from '../../types/consultingProject';
import type { RecentActivityItem } from '../../services/projectService';
import type { Indicator } from '../../types/indicator';
import { getIndicatorHealth } from '../../types/indicator';
import type { CardData, Action } from '../../types';
import {
  ClickableMetric,
  HealthScoreBadge,
  SectionPanel,
  computeProjectScore,
  deriveProjectFinancials,
  fmtDate,
  fmtDateTime,
  fmtCurrency,
  isProjectOverdue,
} from './projectHubShared';

interface ProjectExecutiveSummaryTabProps {
  project: EnterpriseProject;
  summary: ProjectPlansSummary;
  health: ProjectHealth;
  totalActionCost: number;
  validatedGains: number;
  roadmapItems: RoadmapItem[];
  recentActivity: RecentActivityItem[];
  cards: CardData[];
  actions: Action[];
  audits: SustainabilityAudit[];
  orgId: string;
  projectId: string;
  unifiedIndicators: Indicator[];
  andonCritical: boolean;
  paretoFilter: string | null;
  onSetParetoFilter: (id: string | null) => void;
  onRequestBindKpi: () => void;
  onOpenAuditModal: () => void;
  onNavigateTab: (tab: 'actions' | 'roadmap' | 'vsm' | 'management') => void;
  onNavigateOverdueActions: () => void;
  onNavigateCriticalProblems: () => void;
  onNavigateExecutions: () => void;
}

const ACTIVITY_LABELS: Record<RecentActivityItem['type'], string> = {
  action: 'Ação',
  problem: 'Problema',
  workflow: 'Execução',
  indicator: 'Indicador',
};

function toMinutes(value: number | string | undefined): number {
  if (value == null) return 0;
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : 0;
}

export const ProjectExecutiveSummaryTab: React.FC<ProjectExecutiveSummaryTabProps> = ({
  project,
  summary,
  health,
  totalActionCost,
  validatedGains,
  roadmapItems,
  recentActivity,
  cards,
  actions,
  audits,
  orgId,
  projectId,
  unifiedIndicators,
  andonCritical,
  paretoFilter,
  onSetParetoFilter,
  onRequestBindKpi,
  onOpenAuditModal,
  onNavigateTab,
  onNavigateOverdueActions,
  onNavigateCriticalProblems,
  onNavigateExecutions,
}) => {
  const overdue = isProjectOverdue(project.target_date, project.status);
  const score = computeProjectScore(health, summary, overdue);
  const financials = deriveProjectFinancials(summary, totalActionCost, validatedGains);
  const hasFlow = cards.length > 0;
  const leadTime = cards.reduce((sum, c) => sum + toMinutes(c.tp ?? c.lt), 0);
  const tra = cards.reduce((sum, c) => sum + toMinutes(c.tpEfetivo ?? c.tp), 0);
  const pce = leadTime > 0 ? Math.round((tra / leadTime) * 100) : 0;
  const payback = financials.economia > 0 && financials.investimento > 0
    ? Math.max(1, Math.round((financials.investimento / financials.economia) * 12))
    : null;

  const nextMilestones = useMemo(() => (
    [...roadmapItems]
      .filter(i => i.status !== 'completed' && i.status !== 'cancelled')
      .sort((a, b) => new Date(a.plannedStart).getTime() - new Date(b.plannedStart).getTime())
      .slice(0, 5)
  ), [roadmapItems]);

  const timeline = useMemo(
    () => [...recentActivity].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()).slice(0, 6),
    [recentActivity],
  );

  return (
    <div className="flex flex-col gap-2 p-2">
      <div className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-white px-2 py-1.5 dark:border-slate-800 dark:bg-slate-900">
        <HealthScoreBadge score={score} health={health} />
        <div className="flex flex-wrap justify-end gap-1">
          {(overdue || summary.actions_overdue > 0 || summary.issues_critical > 0) && (
            <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[9px] text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
              <AlertTriangle size={9} />
              Atenção operacional
            </span>
          )}
          {andonCritical && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-600 px-1.5 py-0.5 text-[9px] font-bold text-white">
              <Bell size={8} /> ANDON
            </span>
          )}
        </div>
      </div>

      <SectionPanel title="Resultados" icon={<DollarSign size={11} className="text-emerald-500" />}>
        <div className="grid grid-cols-2 gap-1.5 md:grid-cols-5">
          <ClickableMetric label="ROI estimado" value={financials.roiPercent != null ? `${financials.roiPercent}%` : '—'} tone="success" />
          <ClickableMetric label="Economia estimada" value={financials.economia > 0 ? fmtCurrency(financials.economia) : '—'} tone="success" />
          <ClickableMetric label="Payback" value={payback != null ? `${payback}m` : '—'} />
          <ClickableMetric label="Auditorias com nota alta" value={validatedGains > 0 ? `${validatedGains}` : '—'} />
          <ClickableMetric label="Conclusão" value={`${summary.progress}%`} onClick={() => onNavigateTab('actions')} />
          <ClickableMetric label="Ações abertas" value={Math.max(0, summary.actions_total - summary.actions_completed)} onClick={() => onNavigateTab('actions')} />
          <ClickableMetric label="Atrasadas" value={summary.actions_overdue} tone={summary.actions_overdue > 0 ? 'danger' : 'default'} onClick={onNavigateOverdueActions} />
          <ClickableMetric label="Problemas críticos" value={summary.issues_critical} tone={summary.issues_critical > 0 ? 'warning' : 'default'} onClick={onNavigateCriticalProblems} />
          <ClickableMetric label="Execuções" value={summary.executions_completed} onClick={onNavigateExecutions} />
          <ClickableMetric label="Capacidade" value={summary.executions_completed > 0 ? `${summary.executions_completed} ciclos` : '—'} />
        </div>
      </SectionPanel>

      {hasFlow && (
        <SectionPanel title="Operação" icon={<BarChart2 size={11} className="text-blue-500" />}>
          <div className="grid grid-cols-2 gap-1.5 md:grid-cols-5">
            <ClickableMetric label="Lead Time" value={`${leadTime.toFixed(0)}m`} onClick={() => onNavigateTab('vsm')} />
            <ClickableMetric label="PCE" value={`${pce}%`} tone={pce >= 60 ? 'success' : 'warning'} onClick={() => onNavigateTab('vsm')} />
            <ClickableMetric label="Throughput" value={summary.executions_completed} />
            <ClickableMetric label="SLA risco" value={summary.executions_sla_risk} tone={summary.executions_sla_risk > 0 ? 'danger' : 'default'} />
            <ClickableMetric label="WIP" value={actions.filter(a => a.status !== 'done' && a.status !== 'canceled').length} onClick={() => onNavigateTab('actions')} />
          </div>
        </SectionPanel>
      )}

      <SectionPanel title="Sustentação" icon={<Shield size={11} className="text-violet-500" />}>
        <div className="grid grid-cols-2 gap-1.5 md:grid-cols-4">
          <ClickableMetric label="Auditorias" value={audits.length} onClick={onOpenAuditModal} />
          <ClickableMetric label="Aderência" value={audits.length > 0 ? `${Math.round(audits.reduce((s, a) => s + (a.averageScore ?? 0), 0) / audits.length / 5 * 100)}%` : '—'} />
          <ClickableMetric label="Marcos OK" value={roadmapItems.filter(i => i.status === 'completed').length} onClick={() => onNavigateTab('roadmap')} />
          <ClickableMetric label="Padronização" value={summary.progress >= 80 ? 'Alta' : summary.progress >= 50 ? 'Média' : 'Baixa'} />
        </div>
        <div className="mt-1.5 flex items-center gap-2 overflow-x-auto">
          {audits.length === 0 ? (
            <button type="button" onClick={onOpenAuditModal} className="text-[10px] text-blue-600 hover:underline">
              Registrar primeira auditoria
            </button>
          ) : (
            audits.slice(-6).map(a => (
              <span key={a.id} className="shrink-0 rounded border border-slate-200 px-1.5 py-0.5 text-[9px] dark:border-slate-700">
                {(a.averageScore ?? 0).toFixed(1)} · {a.kaizenTitle || 'Auditoria'}
              </span>
            ))
          )}
          <button type="button" onClick={onOpenAuditModal} className="ml-auto inline-flex items-center gap-0.5 text-[9px] text-emerald-700 dark:text-emerald-400">
            <Plus size={9} /> Nova
          </button>
        </div>
      </SectionPanel>

      <div className="grid gap-2 lg:grid-cols-12">
        <div className="lg:col-span-7 space-y-2">
          {unifiedIndicators.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {unifiedIndicators.map(ind => {
                const h = getIndicatorHealth(ind);
                const active = paretoFilter === ind.id;
                return (
                  <button
                    key={ind.id}
                    type="button"
                    onClick={() => onSetParetoFilter(active ? null : ind.id)}
                    className={`flex items-center gap-1 rounded border px-1.5 py-0.5 text-[9px] ${
                      h === 'critical' ? 'border-red-300 text-red-600' : h === 'warning' ? 'border-amber-300 text-amber-600' : 'border-slate-200 text-slate-600'
                    } ${active ? 'bg-blue-50' : ''}`}
                  >
                    {h === 'healthy' ? <CheckCircle2 size={8} /> : h === 'critical' ? <XCircle size={8} /> : <MinusCircle size={8} />}
                    <span className="max-w-[70px] truncate">{ind.name}</span>
                    <span className="font-mono font-bold">{ind.current_value}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="rounded border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center gap-1 border-b border-slate-100 px-2 py-1 dark:border-slate-800">
              <Target size={10} className="text-blue-500" />
              <span className="text-[10px] font-semibold text-slate-700 dark:text-slate-200">Indicadores</span>
            </div>
            <div className="h-[160px] overflow-auto p-1.5">
              <ContextualKPI projectId={projectId} organizationId={orgId} layout="grid" onRequestBindKpi={onRequestBindKpi} />
            </div>
          </div>
        </div>
        <div className="lg:col-span-5 rounded border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <div className="border-b border-slate-100 px-2 py-1 text-[10px] font-semibold dark:border-slate-800">Principais Perdas</div>
          <div className="h-[190px] overflow-hidden p-1.5">
            <BarChartBlueprints />
          </div>
        </div>
      </div>

      <div className="grid gap-2 lg:grid-cols-2">
        <SectionPanel title="Próximos Marcos" icon={<Flag size={11} className="text-violet-500" />}>
          {nextMilestones.length === 0 ? (
            <p className="text-[10px] text-slate-500">
              Sem marcos.{' '}
              <button type="button" onClick={() => onNavigateTab('roadmap')} className="text-blue-600 hover:underline">Estruturar roadmap</button>
            </p>
          ) : (
            <ul className="space-y-1">
              {nextMilestones.map(item => (
                <li key={item.id} className="flex justify-between text-[10px]">
                  <span className="truncate font-medium">{item.title}</span>
                  <span className="font-mono text-slate-500">{fmtDate(item.plannedStart)}</span>
                </li>
              ))}
            </ul>
          )}
        </SectionPanel>
        <SectionPanel title="Movimentações" icon={<Activity size={11} className="text-blue-500" />}>
          {timeline.length === 0 ? (
            <p className="text-[10px] text-slate-500">Sem atividade recente.</p>
          ) : (
            <ul className="space-y-1">
              {timeline.map(item => (
                <li key={`${item.type}-${item.id}`} className="flex gap-1.5 text-[10px]">
                  {item.type === 'problem' ? <Flame size={9} /> : item.type === 'action' ? <ClipboardList size={9} /> : <TrendingUp size={9} />}
                  <span className="truncate">{ACTIVITY_LABELS[item.type]} — {item.title}</span>
                  <span className="ml-auto shrink-0 font-mono text-slate-400">{fmtDateTime(item.updated_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </SectionPanel>
      </div>
    </div>
  );
};
